import type { Components } from 'react-markdown';
import { Children, isValidElement, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { cn } from '../../lib/utils';
import { isMermaidCodeElement, MermaidBlock, mermaidSourceFromCodeElement } from './MermaidBlock';

interface MarkdownPreviewProps {
  content: string;
  className?: string;
  /** Extra readable spacing for chat bubbles (assistant messages). */
  variant?: 'default' | 'chat';
}

function preWithMermaid(
  className: string,
  { children, ...props }: { children?: ReactNode; [k: string]: unknown }
) {
  const arr = Children.toArray(children);
  const first = arr[0];
  if (arr.length === 1 && isMermaidCodeElement(first)) {
    return <MermaidBlock source={mermaidSourceFromCodeElement(first)} />;
  }
  return (
    <pre className={className} {...props}>
      {children}
    </pre>
  );
}

const chatComponents: Components = {
  p: ({ children }) => <p className="mb-2.5 last:mb-0 leading-relaxed">{children}</p>,
  h1: ({ children }) => <h1 className="text-base font-semibold mt-4 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-[15px] font-semibold mt-3.5 mb-2 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h3>,
  ul: ({ children }) => (
    <ul className="my-2 list-disc pl-5 space-y-1 marker:text-gray-400 dark:marker:text-gray-500">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 list-decimal pl-5 space-y-1 marker:text-gray-400 dark:marker:text-gray-500">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed pl-0.5">{children}</li>,
  hr: () => <hr className="my-4 border-gray-300 dark:border-gray-600" />,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-4 border-violet-400/70 pl-3 text-gray-600 dark:text-gray-300 italic">
      {children}
    </blockquote>
  ),
  pre: ({ children, ...props }) =>
    preWithMermaid(
      'my-3 overflow-x-auto rounded-lg border border-gray-600 bg-slate-950 p-3 text-xs leading-relaxed text-slate-100',
      { children, ...props }
    ),
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className?.includes('language-'));
    if (isBlock) {
      return (
        <code className={cn('font-mono text-[12px]', className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-gray-200/90 px-1 py-0.5 font-mono text-[12px] text-gray-900 dark:bg-gray-700/80 dark:text-violet-200"
        {...props}
      >
        {children}
      </code>
    );
  },
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-gray-200 bg-gray-100 px-2 py-1.5 text-left font-semibold dark:border-gray-600 dark:bg-gray-800">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-2 py-1.5 dark:border-gray-700">{children}</td>
  ),
  strong: ({ children }) => <strong className="font-semibold text-gray-900 dark:text-gray-50">{children}</strong>,
};

const defaultComponents: Components = {
  pre: ({ children, ...props }) =>
    preWithMermaid(
      'my-3 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed dark:border-gray-600 dark:bg-slate-950 dark:text-slate-100',
      { children, ...props }
    ),
};

export function MarkdownPreview({ content, className = '', variant = 'default' }: MarkdownPreviewProps) {
  const proseChat =
    variant === 'chat'
      ? [
          'prose-p:my-0',
          'prose-headings:scroll-mt-4',
          'prose-strong:text-inherit',
        ].join(' ')
      : '';

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert',
        'prose-p:leading-relaxed prose-li:leading-relaxed',
        proseChat,
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={variant === 'chat' ? chatComponents : defaultComponents}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}
